import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { Sparkles, Camera, Pencil, Bell, Check, Filter, Moon, Heart } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useTheme } from "@/providers/ThemeProvider";
import { useTranslation } from "react-i18next";

export const AuthLanding = ({ onGetStarted, onSignIn }: { onGetStarted: () => void; onSignIn: () => void }) => {
  const { t } = useTranslation();
  const [metricDefinitions, setMetricDefinitions] = useState<any>({});
  const [allMetrics, setAllMetrics] = useState<any[]>([]);
  const { theme, setTheme } = useTheme();
  const [showFundingModal, setShowFundingModal] = useState(true);

  // Force dark mode for auth landing page
  useEffect(() => {
    const previousTheme = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      // Restore previous theme on unmount
      if (previousTheme !== 'dark') {
        setTheme(previousTheme);
      }
    };
  }, []);

  // Load metrics from database
  useEffect(() => {
    getAllMetrics().then(metrics => {
      const defs = Object.fromEntries(metrics.map(m => [m.key, m]));
      setMetricDefinitions(defs);
      setAllMetrics([
        ...metrics,
        { key: "custom", name: "Custom", label: "Custom", emoji: "💎", unit: "" }
      ]);
    });
  }, []);

  const METRICS = [
    metricDefinitions.protein,
    metricDefinitions.body_weight,
    metricDefinitions.calories,
    metricDefinitions.sleep,
    metricDefinitions.gym_time,
    metricDefinitions.water_gallons,
  ].filter(Boolean); // Filter out undefined values while loading
  const [currentMetricIndex, setCurrentMetricIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [showResult, setShowResult] = useState(false);
  const examples = [
    {
      text: t('auth_landing.examples.0'),
      hasImage: false,
      result: [
        { emoji: "🍗", name: t('metrics.protein'), value: "+58 g", previousProgress: 15, progress: 73 },
        { emoji: "🔥", name: t('metrics.calories'), value: "+420 kcal", previousProgress: 10, progress: 45 },
        { emoji: "🥬", name: t('metrics.fiber'), value: "+8 g", previousProgress: 20, progress: 60 }
      ]
    },
    {
      text: t('auth_landing.examples.1'),
      hasImage: false,
      result: [
        { emoji: "🚬", name: t('auth_landing.custom_metrics.smoking'), value: "0 cigarettes" }
      ]
    },
    {
      text: t('auth_landing.examples.2'),
      hasImage: true,
      result: [
        { emoji: "🍗", name: t('metrics.protein'), value: "+42 g", previousProgress: 30, progress: 72 },
        { emoji: "🔥", name: t('metrics.calories'), value: "+650 kcal", previousProgress: 25, progress: 79 }
      ]
    },
    {
      text: t('auth_landing.examples.3'),
      hasImage: false,
      result: [
        { emoji: "🫀", name: t('auth_landing.custom_metrics.cholesterol'), value: "0 mg" },
        { emoji: "🧈", name: t('auth_landing.custom_metrics.saturated_fat'), value: "0 g" }
      ]
    },
    {
      text: t('auth_landing.examples.4'),
      hasImage: false,
      result: [
        { emoji: "🎹", name: t('auth_landing.custom_metrics.piano_practice'), value: "+25 min", previousProgress: 35, progress: 85 }
      ]
    },
    {
      text: t('auth_landing.examples.5'),
      hasImage: false,
      result: [
        { emoji: "🏃", name: t('auth_landing.custom_metrics.miles'), value: "+3.1 mi", previousProgress: 40, progress: 102 }
      ]
    },
    {
      text: t('auth_landing.examples.6'),
      hasImage: false,
      result: [
        { emoji: "🥬", name: t('metrics.fiber'), value: "0 g" },
        { emoji: "🍗", name: t('metrics.protein'), value: "0 g" },
        { emoji: "🔥", name: t('metrics.calories'), value: "0 kcal" },
        { emoji: "🍬", name: t('auth_landing.custom_metrics.sugar'), value: "0 g" }
      ]
    },
    {
      text: t('auth_landing.examples.7'),
      hasImage: false,
      result: [
        { emoji: "😴", name: t('metrics.sleep'), value: "8.5 hours", previousProgress: 0, progress: 100 }
      ]
    },
    {
      text: t('auth_landing.examples.8'),
      hasImage: false,
      result: [
        { emoji: "🧂", name: t('auth_landing.custom_metrics.sodium'), value: "0 mg" },
        { emoji: "⚙️", name: t('auth_landing.custom_metrics.iron'), value: "0 mg" },
        { emoji: "🔋", name: t('auth_landing.custom_metrics.magnesium'), value: "0 mg" }
      ]
    },
    {
      text: t('auth_landing.examples.9'),
      hasImage: true,
      result: [
        { emoji: "🍗", name: t('metrics.protein'), value: "+52 g", previousProgress: 35, progress: 87 },
        { emoji: "🔥", name: t('metrics.calories'), value: "+285 kcal", previousProgress: 40, progress: 55 },
        { emoji: "🥬", name: t('metrics.fiber'), value: "+4 g", previousProgress: 25, progress: 45 }
      ]
    },
    {
      text: t('auth_landing.examples.10'),
      hasImage: false,
      result: [
        { emoji: "💪", name: t('metrics.gym_time'), value: "+45 min", previousProgress: 25, progress: 100 }
      ]
    },
    {
      text: t('auth_landing.examples.11'),
      hasImage: false,
      result: [
        { emoji: "🍺", name: t('auth_landing.custom_metrics.alcohol'), value: "0 drinks" },
        { emoji: "☕", name: t('auth_landing.custom_metrics.caffeine'), value: "0 mg" }
      ]
    },
    {
      text: t('auth_landing.examples.12'),
      hasImage: false,
      result: [
        { emoji: "⚖️", name: t('metrics.body_weight'), value: "165 lbs" }
      ]
    },
    {
      text: t('auth_landing.examples.13'),
      hasImage: false,
      result: [
        { emoji: "🍬", name: t('auth_landing.custom_metrics.added_sugars'), value: "0 g" },
        { emoji: "🍌", name: t('auth_landing.custom_metrics.potassium'), value: "0 mg" }
      ]
    },
    {
      text: t('auth_landing.examples.14'),
      hasImage: false,
      result: [
        { emoji: "🚭", name: t('auth_landing.custom_metrics.smoke_free'), value: "1", previousProgress: 0, progress: 100 }
      ]
    },
    {
      text: t('auth_landing.examples.15'),
      hasImage: false,
      result: [
        { emoji: "⚽", name: t('auth_landing.custom_metrics.soccer'), value: "0 sessions" },
        { emoji: "🏃", name: t('auth_landing.custom_metrics.runs'), value: "0 mi" }
      ]
    },
    {
      text: t('auth_landing.examples.16'),
      hasImage: false,
      result: [
        { emoji: "⚽", name: t('auth_landing.custom_metrics.soccer'), value: "+2 hours", previousProgress: 40, progress: 140 }
      ]
    },
  ];
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const currentExample = examples[currentExampleIndex];

  // Cycle through metrics and examples
  useEffect(() => {
    // Calculate dynamic cycle time based on current example length
    const typingTime = currentExample.text.length * 70; // 70ms per character
    const delayBeforeResult = 300;
    const resultDisplayTime = 2000; // Time to show results
    const cycleTime = typingTime + delayBeforeResult + resultDisplayTime;

    const timeout = setTimeout(() => {
      setCurrentMetricIndex((prev) => (prev + 1) % METRICS.length);
      setCurrentExampleIndex((prev) => (prev + 1) % examples.length);
    }, cycleTime);

    return () => clearTimeout(timeout);
  }, [currentExampleIndex]);

  // Typing animation
  useEffect(() => {
    setTypedText("");
    setShowResult(false);
    let currentIndex = 0;

    const typingInterval = setInterval(() => {
      if (currentIndex < currentExample.text.length) {
        setTypedText(currentExample.text.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => setShowResult(true), 300); // Faster result display
      }
    }, 70); // Faster typing speed (was 100ms)

    return () => clearInterval(typingInterval);
  }, [currentExampleIndex]);

  const currentMetric = METRICS[currentMetricIndex];

  // Generate fake contribution data
  const generateFakeData = () => {
    return Array.from({ length: 52 }, (_, week) =>
      Array.from({ length: 7 }, (_, day) => {
        const random = Math.random();
        return random > 0.3 ? Math.floor(random * 4) + 1 : 0;
      })
    );
  };

  const [contributionData] = useState(generateFakeData());

  return (
    <>
    <div className="min-h-screen bg-background flex flex-col items-center justify-center py-8 px-4 relative overflow-hidden select-none">

      <div className="w-full max-w-2xl relative z-10 flex flex-col items-center gap-6">
        {/* Contribution Chart - Above Hero */}
        <div className="relative opacity-40">
          <div className="flex gap-0.5">
            {contributionData.slice(0, 12).map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-0.5">
                {week.slice(0, 3).map((intensity, dayIndex) => {
                  const colors = {
                    0: "hsl(var(--muted))",
                    1: "hsl(142 30% 85%)",
                    2: "hsl(142 40% 65%)",
                    3: "hsl(142 50% 45%)",
                    4: "hsl(142 60% 35%)",
                  };
                  return (
                    <div
                      key={dayIndex}
                      className="w-2 h-2 rounded-sm"
                      style={{ backgroundColor: colors[intensity as keyof typeof colors] }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Hero Section */}
        <div className="text-center">
          <h1 className="text-4xl max-[375px]:text-3xl font-bold tracking-tight mb-2 max-[375px]:mb-1.5">{t('auth.welcome')}</h1>
          <div className="flex items-center justify-center gap-2 text-xs max-[375px]:text-[11px] text-muted-foreground/60 mb-2 max-[375px]:mb-1.5">
            <Sparkles className="w-3 h-3 max-[375px]:w-2.5 max-[375px]:h-2.5" />
            <p>{t('auth.track_daily')}</p>
          </div>

          {/* Scrolling Metrics */}
          <div className="w-full max-w-md mx-auto opacity-60">
            <div
              className="relative w-full overflow-hidden h-8"
              style={{
                maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)"
              }}
            >
              <motion.div
                className="flex gap-8 absolute"
                animate={{
                  x: [0, -1200],
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 40,
                    ease: "linear",
                  },
                }}
              >
                {[...allMetrics, ...allMetrics].map((metric, idx) => {
                  const isBoolean = metric.type === "boolean";
                  const seed = idx % allMetrics.length;
                  const randomValue = isBoolean
                    ? (seed % 2 === 0 ? 100 : 0)
                    : 40 + (seed * 7) % 60;

                  const radius = 12;
                  const circumference = 2 * Math.PI * radius;
                  const strokeDashoffset = circumference - (randomValue / 100) * circumference;

                  const getProgressColor = (percentage: number) => {
                    if (percentage >= 90) return "hsl(142 60% 35%)";
                    if (percentage >= 70) return "hsl(142 50% 45%)";
                    if (percentage >= 50) return "hsl(60 90% 50%)";
                    if (percentage >= 30) return "hsl(30 90% 55%)";
                    return "hsl(0 80% 55%)";
                  };

                  return (
                    <div key={idx} className="flex-shrink-0">
                      <svg className="w-8 h-8" viewBox="0 0 32 32">
                        <circle
                          cx="16"
                          cy="16"
                          r={radius}
                          stroke="hsl(var(--muted))"
                          strokeWidth="1.5"
                          fill="none"
                          transform="rotate(-90 16 16)"
                        />
                        <circle
                          cx="16"
                          cy="16"
                          r={radius}
                          stroke={getProgressColor(randomValue)}
                          strokeWidth="1.5"
                          fill="none"
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                          strokeLinecap="round"
                          transform="rotate(-90 16 16)"
                          style={!isBoolean ? {
                            transition: "stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                          } : {}}
                        />
                        <text x="16" y="20" textAnchor="middle" fontSize="14" fill="currentColor">
                          {metric.emoji}
                        </text>
                      </svg>
                    </div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </div>

        {/* AI Input Demo - Fixed height container */}
        <div className="w-full max-w-md mx-auto h-[180px] pointer-events-none select-none">
          <div className="relative">
            <motion.div
              className="bg-card/50 border rounded-lg shadow-sm overflow-hidden"
              animate={{
                height: showResult ? "auto" : "auto"
              }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={typedText}
                      readOnly
                      tabIndex={-1}
                      className="w-full bg-transparent border-none outline-none text-sm pointer-events-none select-none"
                      placeholder={t('auth_landing.input_placeholder')}
                    />
                    {typedText && !showResult && (
                      <span className="absolute left-0 top-[calc(50%+1px)] -translate-y-1/2 pointer-events-none text-sm">
                        <span className="invisible">{typedText}</span>
                        <span className="inline-block w-0.5 h-4 bg-foreground ml-0.5 caret-blink" />
                      </span>
                    )}
                  </div>
                  {currentExample.hasImage && typedText && (
                    <motion.span
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-[11px] text-muted-foreground/70 mr-0.5"
                    >
                      {t('auth.image_attached')}
                    </motion.span>
                  )}
                  <button className="p-1.5 rounded-md hover:bg-muted/50 transition-colors flex-shrink-0 opacity-60 relative" tabIndex={-1}>
                    <Camera className="w-4 h-4" />
                    <AnimatePresence>
                      {currentExample.hasImage && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: [0, 1.3, 1] }}
                          exit={{ scale: 0 }}
                          transition={{ duration: 0.5, times: [0, 0.6, 1], ease: "easeOut" }}
                          className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full"
                        />
                      )}
                    </AnimatePresence>
                  </button>
                  <AnimatePresence>
                    {(typedText.length >= 5 || currentExampleIndex === 2) && (
                      <motion.button
                        initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                        animate={{
                          opacity: 1,
                          width: "2rem",
                          marginLeft: "0.25rem",
                          scale: showResult ? [1, 0.85, 1] : 1
                        }}
                        exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                        transition={{
                          duration: 0.4,
                          ease: [0.4, 0, 0.2, 1],
                          opacity: { duration: 0.3 },
                          scale: { duration: 0.2, times: [0, 0.5, 1] }
                        }}
                        className="h-8 w-8 rounded-full ring-1 ring-accent/30 bg-gradient-to-r from-accent/60 to-accent/80 hover:from-accent/70 hover:to-accent/90 transition-colors flex-shrink-0 overflow-hidden flex items-center justify-center"
                        tabIndex={-1}
                      >
                        <Sparkles className="w-4 h-4 text-white" />
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* AI Results - Inline expansion */}
              <AnimatePresence initial={false} mode="wait">
                {showResult && (
                  <motion.div
                    key={currentExampleIndex}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-3 border-t text-xs space-y-1.5">
                      {currentExample.result.map((item, idx) => (
                        <motion.div
                          key={idx}
                          className="flex items-center gap-2"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                        >
                          <span className="flex items-center gap-1.5">
                            {item.emoji} {item.name}:
                            <span className="font-medium">{item.value}</span>
                            <Pencil className="w-3 h-3 text-muted-foreground/40" />
                          </span>
                          {typeof item.progress === 'number' && item.progress >= 100 ? (
                            <motion.span
                              className="ml-auto"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: idx * 0.1 + 0.2 }}
                            >
                              <Check className="w-4 h-4 text-accent" strokeWidth={3} />
                            </motion.span>
                          ) : typeof item.progress === 'number' ? (
                            <motion.div
                              className="ml-auto w-24"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: idx * 0.1 + 0.2 }}
                            >
                              <Progress value={item.progress} previousValue={item.previousProgress} className="h-2" />
                            </motion.div>
                          ) : null}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>

        {/* Spacer before CTA */}

        {/* Primary CTA Section with Benefits */}
        <div className="w-full max-w-md space-y-4 -mt-[28px]">
          <Button onClick={onGetStarted} size="lg" className="w-full bg-gradient-to-bl from-accent via-accent to-accent/80 hover:from-accent/90 hover:via-accent/90 hover:to-accent/70 text-accent-foreground font-semibold shadow-md">
            {t('auth.start_tracking_free')}
          </Button>

          {/* Trust Signals */}
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground/50">
            <span className="flex items-center gap-1">
              {t('auth.no_credit_card')}
            </span>
            <span className="flex items-center gap-1">
              {t('auth.data_private')}
            </span>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            {t('auth.or')}{" "}
            <button onClick={onSignIn} className="text-foreground hover:text-accent underline font-medium">
              {t('auth.sign_in')}
            </button>
          </p>
        </div>

        {/* Key Benefits */}
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-5 max-[375px]:gap-4">
            {/* Personalize */}
            <div className="flex flex-col items-center gap-2.5 max-[375px]:gap-2">
              <p className="text-muted-foreground/70 text-center text-sm max-[375px]:text-xs font-light h-7 max-[375px]:h-6 flex items-center">{t('auth.personalize')}</p>
              <div className="flex flex-col items-center gap-1 max-[375px]:gap-0.5">
                <p className="text-xs max-[375px]:text-[11px] text-muted-foreground/60 font-thin text-center">{t('auth.zero_noise')}</p>
                <Filter className="w-4 h-4 max-[375px]:w-3.5 max-[375px]:h-3.5 text-muted-foreground/40" />
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="h-14 max-[375px]:h-12 w-px bg-border/50" />

            {/* Optimize */}
            <div className="flex flex-col items-center gap-2.5 max-[375px]:gap-2">
              <p className="text-muted-foreground/70 text-center text-sm max-[375px]:text-xs font-light h-7 max-[375px]:h-6 flex items-center">{t('auth.optimize')}</p>
              <div className="flex flex-col items-center gap-1 max-[375px]:gap-0.5">
                <p className="text-xs max-[375px]:text-[11px] text-muted-foreground/60 font-thin text-center">{t('auth.micro_suggestions')}</p>
                <Moon className="w-4 h-4 max-[375px]:w-3.5 max-[375px]:h-3.5 text-muted-foreground/40" />
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    {/* Funding Modal - blocks all interaction */}
    <AnimatePresence>
      {showFundingModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="w-full max-w-sm bg-card border rounded-2xl shadow-2xl p-6 flex flex-col items-center gap-4 text-center"
          >
            <div className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center">
              <Heart className="w-6 h-6 text-accent" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold">{t('auth_landing.funding_modal.title')}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('auth_landing.funding_modal.message')}
              </p>
            </div>
            <a
              href="https://buymeacoffee.com/aaronmichaq?status=1"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button className="w-full bg-[#FFDD00] hover:bg-[#FFDD00]/90 text-black font-semibold gap-2">
                ☕ {t('auth_landing.funding_modal.cta')}
              </Button>
            </a>
            <button
              onClick={() => setShowFundingModal(false)}
              className="text-xs text-muted-foreground/60 hover:text-muted-foreground underline transition-colors"
            >
              {t('auth_landing.funding_modal.dismiss')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
